function debugEnabled() {
    try {
        const p = globalThis?.process?.env?.DEBUG_HTTP;
        if (p && String(p).toLowerCase() === 'true')
            return true;
        const w = globalThis?.__ENV__?.DEBUG_HTTP;
        if (w && String(w).toLowerCase() === 'true')
            return true;
    }
    catch { }
    return false;
}
export class GameWs {
    ws;
    // suppress toast on next close when we intentionally close (idle/manual)
    suppressNextCloseNotify = false;
    pingTimer;
    onAction;
    url;
    openResolvers = [];
    hasNotifiedClose = false;
    sessionReady = false;
    sessionReadyResolvers = [];
    // Track who initiated the close for diagnostics
    clientInitiatedClose = false;
    lastClientCloseTag;
    // Track script id to attach in every text_message
    script_id;
    constructor(opts) {
        const base = opts.baseUrl.replace(/\/$/, '');
        const qs = new URLSearchParams();
        if (opts.token)
            qs.set('token', opts.token);
        const suffix = qs.toString() ? `?${qs.toString()}` : '';
        this.url = `${base}/ws/roleplay/${encodeURIComponent(opts.userId)}${suffix}`;
        if (debugEnabled()) {
            try {
                console.log('[WS] URL =', this.url);
            }
            catch { }
        }
    }
    connect(onAction, options) {
        this.onAction = onAction;
        this.ws = new WebSocket(this.url);
        this.hasNotifiedClose = false;
        this.sessionReady = false;
        this.clientInitiatedClose = false;
        this.lastClientCloseTag = undefined;
        this.ws.onmessage = (ev) => {
            try {
                const msg = JSON.parse(ev.data);
                if (Array.isArray(msg.actions)) {
                    msg.actions.forEach((a) => {
                        // mark session ready on ready/start events
                        this.checkSessionReady(a);
                        this.onAction && this.onAction(a);
                    });
                    return;
                }
                // Server error envelope e.g. { success: false, error: { code, message } }
                if (msg && (msg.success === false || msg.error)) {
                    this.onAction && this.onAction({ type: 'server_error', data: msg });
                    return;
                }
                // Fallback: if message has a type, forward directly
                if (msg && typeof msg.type === 'string') {
                    this.checkSessionReady(msg);
                    this.onAction && this.onAction(msg);
                }
            }
            catch (e) {
                console.error('WS parse error', e, ev.data);
            }
        };
        this.ws.onopen = () => {
            try {
                options?.onOpen?.();
            }
            catch { }
            // Optional auth or ping
            if (debugEnabled()) {
                try {
                    console.log('[WS] open');
                }
                catch { }
            }
            // Keep-alive ping to prevent server idle timeouts
            try {
                if (this.pingTimer)
                    window.clearInterval(this.pingTimer);
            }
            catch { }
            this.pingTimer = window.setInterval(() => {
                try {
                    this.send({ type: 'ping' });
                }
                catch { }
            }, 25000);
            // Start session with active script if provided
            if (options?.startScriptId) {
                // Remember script id for subsequent text_message payloads
                this.script_id = options.startScriptId;
                try {
                    const payload = {
                        type: 'start_session',
                        script_id: options.startScriptId,
                        character_id: 'player',
                        language: options.language,
                        model: options.model,
                    };
                    if (options.sessionId) {
                        payload.session_id = options.sessionId;
                    }
                    else {
                        payload.is_new = true;
                    }
                    if (options.shareToken) {
                        payload.share_token = options.shareToken;
                    }
                    if (debugEnabled()) {
                        try {
                            console.log('[WS] send start_session', payload);
                        }
                        catch { }
                    }
                    this.send(payload);
                }
                catch (e) {
                    console.error('Failed to send start_session', e);
                }
            }
            // resolve waiters
            const resolvers = this.openResolvers.slice();
            this.openResolvers = [];
            resolvers.forEach((r) => { try {
                r();
            }
            catch { } });
        };
        this.ws.onerror = (ev) => {
            // Suppress global error toast; rely on in-UI status + manual retry
            try {
                options?.onError?.(ev);
            }
            catch { }
            if (debugEnabled()) {
                try {
                    console.log('[WS] error', ev);
                }
                catch { }
            }
        };
        this.ws.onclose = (ev) => {
            try {
                const initiator = this.clientInitiatedClose ? `client:${this.lastClientCloseTag || 'manual'}` : 'remote/server';
                console.warn('[WS] close', { initiator, code: ev?.code, reason: ev?.reason, wasClean: ev?.wasClean });
            }
            catch { }
            if (this.pingTimer) {
                try {
                    window.clearInterval(this.pingTimer);
                }
                catch { }
                this.pingTimer = undefined;
            }
            // Suppress global close toast; UI will show status banner and retry controls
            this.hasNotifiedClose = true;
            // reset suppression flag so subsequent unexpected closes can notify
            this.suppressNextCloseNotify = false;
            // reset session ready and pending resolvers
            this.sessionReady = false;
            const rs = this.sessionReadyResolvers.slice();
            this.sessionReadyResolvers = [];
            rs.forEach((r) => { try {
                r();
            }
            catch { } });
            try {
                options?.onClose?.();
            }
            catch { }
        };
    }
    send(obj) {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN)
            throw new Error('WebSocket not open');
        this.ws.send(JSON.stringify(obj));
    }
    sendText(text, language, sessionId, model, history, globals) {
        const payload = { type: 'text_message', message: text, language, session_id: sessionId, model };
        if (this.script_id)
            payload.script_id = this.script_id;
        if (Array.isArray(history))
            payload.history = history;
        if (globals && typeof globals === 'object')
            payload.globals = globals;
        this.send(payload);
    }
    // Close the socket. Overload: close('reasonTag', silent?) or close(silent?)
    close(silentOrReason, maybeSilent) {
        try {
            let silent = false;
            if (typeof silentOrReason === 'string') {
                this.lastClientCloseTag = silentOrReason;
                silent = !!maybeSilent;
            }
            else {
                silent = !!silentOrReason;
                this.lastClientCloseTag = 'manual';
            }
            this.clientInitiatedClose = true;
            if (silent)
                this.suppressNextCloseNotify = true;
            this.ws?.close();
        }
        catch { }
    }
    isOpen() {
        return !!this.ws && this.ws.readyState === WebSocket.OPEN;
    }
    waitUntilOpen() {
        if (this.isOpen())
            return Promise.resolve();
        return new Promise((resolve) => {
            this.openResolvers.push(resolve);
        });
    }
    checkSessionReady(action) {
        try {
            const t = action?.type;
            if (t === 'session_ready' || t === 'session_started') {
                this.sessionReady = true;
                const rs = this.sessionReadyResolvers.slice();
                this.sessionReadyResolvers = [];
                rs.forEach((r) => { try {
                    r();
                }
                catch { } });
            }
        }
        catch { }
    }
    waitUntilSessionReady() {
        if (this.sessionReady)
            return Promise.resolve();
        return new Promise((resolve) => {
            this.sessionReadyResolvers.push(resolve);
        });
    }
}
